import type { SpecType } from '@local/lib';

import {
  Box,
  Button,
  Divider,
  Group,
  Paper,
  Pill,
  Popover,
  Stack,
  Text,
  Title,
  Tooltip,
  Transition,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { RotateCcw } from 'lucide-react';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { TemplateSpecConfig } from './qualityFormulaTypes';

import { PageMeta, QualityAdjustmentsNav } from '../../components';
import { LoadTemplateControl } from '../templates/components/LoadTemplateControl';
import { ContractPricingSummarySection } from './components/ContractPricingSummarySection';
import { FormulaTestingPanel } from './components/FormulaTestingPanel';
import { QualityResultsSection } from './components/QualityResultsSection';
import { QualitySpecCard } from './components/QualitySpecCard';
import { QualityTypeFilter } from './components/QualityTypeFilter';
import { SPEC_TYPES } from './qualityFormulaMetadata';
import { QualityFormulaStoreProvider, useQualityFormulaStore } from './store/QualityFormulaStoreProvider';

// Stable across every render (no closure over page state) so it never invalidates `QualitySpecCard`'s
// `memo` — an inline arrow function recreated on every `QualityFormulasWorkspace` render would give
// each card a new `renderLoadTemplateControl` prop identity every time, defeating the memo. The
// applied-config callback comes from the card itself (via `onApply`), not a closure here, so this
// function never needs store access of its own.
const renderLoadTemplateControl = (
  specType: SpecType,
  specLabel: string,
  onApply: (config: TemplateSpecConfig) => void,
): React.ReactNode => <LoadTemplateControl onApply={onApply} specLabel={specLabel} specType={specType} />;

const ResetInputsButton: React.FC<{ variant?: 'filled' | 'outline' }> = ({ variant = 'filled' }) => {
  const handleResetInputs = useQualityFormulaStore(store => store.handleResetInputs);
  const [confirmOpened, { close: closeConfirm, toggle: toggleConfirm }] = useDisclosure(false);

  return (
    <Popover onClose={closeConfirm} opened={confirmOpened} position="bottom-end" shadow="md" withArrow>
      <Popover.Target>
        <Tooltip label="Clears entered field values and test values" position="bottom">
          <Button
            aria-label="Reset entered values"
            color={variant === 'outline' ? 'blue.5' : 'blue.8'}
            leftSection={<RotateCcw size={16} />}
            onClick={toggleConfirm}
            radius="xl"
            size="sm"
            style={variant === 'outline' ? { borderWidth: 2 } : undefined}
            variant={variant}
          >
            Reset
          </Button>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown maw={260}>
        <Stack gap="xs">
          <Text fz="sm">Clear entered field values for the specs and actual test values? This can’t be undone.</Text>
          <Group gap="xs" justify="flex-end">
            <Button color="gray.6" onClick={closeConfirm} size="xs" variant="filled">
              Cancel
            </Button>
            <Button
              color="red.7"
              onClick={() => {
                handleResetInputs();
                closeConfirm();
              }}
              size="xs"
              variant="filled"
            >
              Reset
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

interface SpecCardSlotProps {
  hint: string;
  icon: string;
  specLabel: string;
  specType: SpecType;
}

// Reads only this one spec's `enabled` flag (rather than the page handing down the whole
// `specStates` record) so toggling/editing one card's fields never re-renders this slot, let alone
// the page, for every other spec — only its own Transition mounts/unmounts in response.
const SpecCardSlot: React.FC<SpecCardSlotProps> = ({ hint, icon, specLabel, specType }) => {
  const enabled = useQualityFormulaStore(store => store.specStates[specType].enabled);

  return (
    // Every spec renders a permanently-mounted Transition so toggling `enabled` (via the filter)
    // plays Mantine's enter/exit animation — a freshly-created Transition instance only animates
    // between renders it's present for, not on its own first mount.
    <Transition duration={220} mounted={enabled} transition="pop">
      {styles => (
        <Box id={`spec-card-${specType}`} style={styles}>
          <QualitySpecCard
            hint={hint}
            icon={icon}
            renderLoadTemplateControl={renderLoadTemplateControl}
            specLabel={specLabel}
            specType={specType}
          />
        </Box>
      )}
    </Transition>
  );
};

const QualityFormulasWorkspace: React.FC = () => {
  const { handleVisibleSpecTypesChange, visibleSpecTypes } = useQualityFormulaStore(
    useShallow(store => ({
      handleVisibleSpecTypesChange: store.handleVisibleSpecTypesChange,
      visibleSpecTypes: store.visibleSpecTypes,
    })),
  );
  // Selected separately from `handleVisibleSpecTypesChange`/`visibleSpecTypes` above (rather than
  // reading the whole `specStates` record and deriving these inline) so this page only re-renders
  // when a spec's enabled-set or expanded-state actually changes membership — not on every
  // keystroke in any card's row fields, which changes `specStates`' reference but not these two
  // derived values. `enabledSpecs`' elements come from the stable `SPEC_TYPES` array, so `useShallow`
  // correctly bails out when the filtered set is unchanged; `hasExpandedSpec` is a plain boolean, so
  // Zustand's default `Object.is` equality already does the same.
  const enabledSpecs = useQualityFormulaStore(
    useShallow(store => SPEC_TYPES.filter(spec => store.specStates[spec.key].enabled)),
  );
  const hasExpandedSpec = useQualityFormulaStore(store =>
    SPEC_TYPES.some(spec => store.specStates[spec.key].enabled && !store.specStates[spec.key].collapsed),
  );
  // Gates the Divider preceding QualityResultsSection, which otherwise renders nothing until a
  // formula has been tested (see that component's own doc comment).
  const hasPreviewResults = useQualityFormulaStore(store => store.results.length > 0 && store.summary !== null);

  const orderedSpecTypes = useMemo(
    () => SPEC_TYPES.filter(spec => visibleSpecTypes.includes(spec.key)),
    [visibleSpecTypes],
  );

  const handleSpecSelectionChange = (nextVisibleTypes: SpecType[]) => {
    handleVisibleSpecTypesChange(nextVisibleTypes);
  };

  return (
    <Box maw={1180} mx="auto" pb="xl" pt="sm" px="md" w="100%">
      <PageMeta
        description="Build and review quality pricing formulas in a guided, interactive playground."
        title="Quality Pricing Formula"
      />
      <Stack gap="lg">
        <QualityAdjustmentsNav />
        <Stack gap="sm">
          <Group align="flex-start" justify="space-between" wrap="wrap">
            <Stack gap="xs" maw={520}>
              <Title fz={{ base: 22, sm: 28 }} order={1}>
                Quality Pricing Formula
              </Title>
              <Text c="dimmed" size="sm">
                Build price and quantity adjustment rules for each quality specification, then instantly preview the
                results against the contract shown below with sample values — no changes are saved.
              </Text>
            </Stack>
          </Group>

          <Divider />
        </Stack>

        <ContractPricingSummarySection />

        <Divider />

        <Stack aria-label="Select and configure quality specifications" component="section" gap="md">
          <Stack gap={4}>
            <Title order={2} size="h4">
              Select &amp; Configure Quality Specs
            </Title>
            <Text c="dimmed" fz="sm" maw={640}>
              Select the specs you want to test from the filter, then choose how each one should affect price or
              delivered quantity. Click a spec's header to collapse or expand it and save scrolling.
            </Text>
          </Stack>

          <Paper bg="var(--mantine-color-default-hover)" p="sm" radius="md" withBorder>
            <Group align="center" gap="sm" justify="space-between" wrap="wrap">
              <Group gap={8} wrap="wrap">
                <Text c="dimmed" fw={700} fz={11} lts={0.3} tt="uppercase">
                  Quality Types
                </Text>
                {orderedSpecTypes.length === 0 ? (
                  <Text c="dimmed" fz="sm">
                    None selected
                  </Text>
                ) : (
                  <Pill.Group>
                    {orderedSpecTypes.map(spec => (
                      <Pill
                        key={spec.key}
                        onRemove={() => {
                          handleSpecSelectionChange(visibleSpecTypes.filter(key => key !== spec.key));
                        }}
                        size="sm"
                        withRemoveButton
                      >
                        {spec.label}
                      </Pill>
                    ))}
                  </Pill.Group>
                )}
              </Group>
              <Group gap={8} wrap="nowrap">
                <QualityTypeFilter
                  onChange={handleSpecSelectionChange}
                  specTypes={SPEC_TYPES}
                  value={visibleSpecTypes}
                />
                <Button
                  disabled={visibleSpecTypes.length === 0}
                  onClick={() => {
                    handleSpecSelectionChange([]);
                  }}
                  size="xs"
                  variant="default"
                >
                  Clear
                </Button>
              </Group>
            </Group>
          </Paper>

          <Stack aria-label="Quality specification formulas" gap={hasExpandedSpec ? 'md' : 'xs'}>
            {SPEC_TYPES.map(spec => (
              <SpecCardSlot
                hint={spec.hint}
                icon={spec.icon}
                key={spec.key}
                specLabel={spec.label}
                specType={spec.key}
              />
            ))}
          </Stack>
        </Stack>

        {enabledSpecs.length > 0 ? (
          <>
            <Divider />
            <FormulaTestingPanel
              description="Enter sample measured values to see how each configured spec would change price or delivered quantity."
              enabledSpecs={enabledSpecs}
              extraActions={<ResetInputsButton variant="outline" />}
              testButtonLabel="Test These Formulas"
              title="Test Formula"
            />
          </>
        ) : null}
        {hasPreviewResults ? (
          <>
            <Divider />
            <QualityResultsSection />
          </>
        ) : null}
      </Stack>
    </Box>
  );
};

export const QualityFormulasPage: React.FC = () => (
  <QualityFormulaStoreProvider>
    <QualityFormulasWorkspace />
  </QualityFormulaStoreProvider>
);
